import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, ShieldCheck, Lock, Truck, CreditCard, AlertCircle, Tag, Check, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { items, total, clearCart, markConverted } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [squareLoading, setSquareLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [shippingConfig, setShippingConfig] = useState({ flat_rate: 5.99, free_shipping_threshold: 50 });
  const [squareConfig, setSquareConfig] = useState(null);

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');

  const cardInstanceRef = useRef(null);
  const orderRef = useRef(null);

  const [shipping, setShipping] = useState({
    firstName: '', lastName: '', email: '',
    address: '', city: '', state: '', zip: '', country: 'United States'
  });

  useEffect(() => {
    SEO.applyNoIndex('Checkout | PosterFire', '/checkout');
    return () => SEO.resetDefaults();
  }, []);

  useEffect(() => {
    axios.get(`${API}/shipping-config`).then(r => setShippingConfig(r.data)).catch(() => {});
    axios.get(`${API}/square/config`).then(r => setSquareConfig(r.data)).catch(() => {});
  }, []);

  const handleShippingChange = (e) => {
    setShipping({ ...shipping, [e.target.name]: e.target.value });
  };

  const validateShipping = () => {
    return ['firstName', 'lastName', 'email', 'address', 'city', 'state', 'zip'].every(f => shipping[f]?.trim());
  };

  // Apply promo code
  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const res = await axios.post(`${API}/validate-promo-code`, {
        code: promoInput.trim(),
        email: shipping.email,
        subtotal: total
      });
      setPromoApplied(res.data);
      toast.success(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid promo code';
      setPromoError(msg);
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoInput('');
    setPromoError('');
  };

  // Initialize Square card form when step 2 is reached
  const initSquareCard = useCallback(async () => {
    if (cardInstanceRef.current) return;
    if (!squareConfig) return;
    setSquareLoading(true);
    setPaymentError('');

    try {
      const res = await axios.post(`${API}/square/create-checkout`, {
        items: items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size
        })),
        shipping_address: shipping,
        email: shipping.email,
        promo_code: promoApplied?.code || ''
      });
      orderRef.current = res.data;

      if (!window.Square) {
        await new Promise((resolve, reject) => {
          const existing = document.querySelector('script[src*="squarecdn.com"]');
          if (existing) { existing.remove(); } // Remove old SDK if env changed
          const script = document.createElement('script');
          const sdkUrl = squareConfig.environment === 'production'
            ? 'https://web.squarecdn.com/v1/square.js'
            : 'https://sandbox.web.squarecdn.com/v1/square.js';
          script.src = sdkUrl;
          script.async = true;
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load Square payment SDK'));
          document.head.appendChild(script);
        });
      }

      const payments = window.Square.payments(squareConfig.application_id, squareConfig.location_id);
      const card = await payments.card();
      await card.attach('#square-card-container');
      cardInstanceRef.current = card;
      setCardReady(true);
    } catch (err) {
      console.error('Square init error:', err);
      setPaymentError(err.response?.data?.detail || err.message || 'Failed to load payment form');
    } finally {
      setSquareLoading(false);
    }
  }, [items, shipping, promoApplied, squareConfig]);

  useEffect(() => {
    if (step === 2) {
      const timer = setTimeout(() => initSquareCard(), 100);
      return () => clearTimeout(timer);
    }
  }, [step, initSquareCard]);

  useEffect(() => {
    return () => {
      if (cardInstanceRef.current) {
        cardInstanceRef.current.destroy?.();
        cardInstanceRef.current = null;
      }
    };
  }, []);

  const handleSquarePayment = async () => {
    if (!cardInstanceRef.current || !orderRef.current) return;
    setLoading(true);
    setPaymentError('');

    try {
      const tokenResult = await cardInstanceRef.current.tokenize();
      if (tokenResult.status !== 'OK') {
        const errorMsg = tokenResult.errors?.map(e => e.message).join(', ') || 'Card verification failed';
        setPaymentError(errorMsg);
        setLoading(false);
        return;
      }

      const res = await axios.post(`${API}/square/process-payment`, {
        order_id: orderRef.current.order_id,
        source_id: tokenResult.token
      });

      if (res.data.status === 'COMPLETED') {
        const successUrl = `/checkout/success?method=square&order_id=${orderRef.current.order_id}`;
        navigate(successUrl, { replace: true });
        markConverted();
        clearCart();
      } else {
        setPaymentError('Payment was not completed. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      const detail = err.response?.data?.detail;
      setPaymentError(typeof detail === 'string' ? detail : 'Payment failed. Please check your card and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Redirect to cart if empty (but not after a successful payment)
  useEffect(() => {
    if (items.length === 0 && !window.location.search.includes('success')) {
      navigate('/cart');
    }
  }, [items.length, navigate]);

  if (items.length === 0) return null;

  const discount = promoApplied?.discount_amount || 0;
  const subtotalAfterDiscount = Math.max(0, total - discount);
  const shippingCost = subtotalAfterDiscount >= shippingConfig.free_shipping_threshold ? 0 : shippingConfig.flat_rate;
  const finalTotal = subtotalAfterDiscount + shippingCost;

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      <div className="h-1 bg-gradient-to-r from-red-500 via-orange-500 to-amber-400" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/cart')}
          className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-8"
          data-testid="back-to-cart">
          <ArrowLeft className="w-4 h-4" /> Back to Cart
        </button>

        <div className="flex items-center gap-0 mb-10 max-w-md">
          <div className="flex items-center gap-2">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 1 ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>1</span>
            <span className={`text-sm font-semibold tracking-wide ${step >= 1 ? 'text-gray-900' : 'text-gray-400'}`}>Shipping</span>
          </div>
          <div className={`flex-1 h-0.5 mx-4 ${step >= 2 ? 'bg-gradient-to-r from-orange-500 to-amber-400' : 'bg-gray-200'}`} />
          <div className="flex items-center gap-2">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 2 ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>2</span>
            <span className={`text-sm font-semibold tracking-wide ${step >= 2 ? 'text-gray-900' : 'text-gray-400'}`}>Payment</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            {step === 1 && (
              <div data-testid="shipping-form">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Shipping Address</h2>
                <p className="text-sm text-gray-500 mb-4">Checking out as guest — no account needed.</p>
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                      <input name="firstName" value={shipping.firstName} onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="John" data-testid="shipping-firstName" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                      <input name="lastName" value={shipping.lastName} onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Doe" data-testid="shipping-lastName" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input type="email" name="email" value={shipping.email} onChange={handleShippingChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="john@email.com" data-testid="shipping-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Street Address</label>
                    <input name="address" value={shipping.address} onChange={handleShippingChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="123 Main St" data-testid="shipping-address" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">City</label>
                      <input name="city" value={shipping.city} onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="New York" data-testid="shipping-city" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">State</label>
                      <input name="state" value={shipping.state} onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="NY" data-testid="shipping-state" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">ZIP Code</label>
                      <input name="zip" value={shipping.zip} onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="10001" data-testid="shipping-zip" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
                    <input name="country" value={shipping.country} disabled
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-500 text-sm bg-gray-50"
                      data-testid="shipping-country" />
                  </div>
                  <button onClick={() => { if (validateShipping()) setStep(2); else toast.error('Please fill in all fields'); }}
                    className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-red-500 to-orange-500 text-white font-semibold rounded-lg hover:from-red-600 hover:to-orange-600 transition-all shadow-lg shadow-orange-500/20"
                    data-testid="continue-to-payment">
                    Continue to Payment
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="payment-form">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment</h2>

                <div className="w-full p-5 border-2 border-gray-900 bg-gray-50 rounded-xl mb-6" data-testid="square-payment-info">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm">Pay with Card</p>
                      <p className="text-xs text-gray-500">Credit Card, Debit Card — Powered by Square</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  {squareLoading && (
                    <div className="flex items-center justify-center py-10 bg-gray-50 rounded-xl border border-gray-200">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-3" />
                      <span className="text-sm text-gray-500">Loading payment form...</span>
                    </div>
                  )}
                  <div
                    id="square-card-container"
                    data-testid="square-card-container"
                    className={`min-h-[80px] rounded-xl ${squareLoading ? 'hidden' : ''}`}
                  />
                </div>

                {paymentError && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl mb-6" data-testid="payment-error">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{paymentError}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                  <Lock className="w-4 h-4" />
                  <span>Your card details are encrypted and secured by Square</span>
                </div>

                <div className="flex gap-4">
                  <button onClick={() => setStep(1)}
                    className="px-6 py-3.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    data-testid="back-to-shipping">
                    Back
                  </button>
                  <button
                    onClick={handleSquarePayment}
                    disabled={loading || !cardReady}
                    className="flex-1 px-8 py-3.5 font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-all shadow-lg bg-gray-900 text-white hover:bg-gray-800 shadow-gray-900/20"
                    data-testid="place-order-btn">
                    {loading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                    ) : (
                      <>Pay ${finalTotal.toFixed(2)}</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sticky top-24" data-testid="checkout-summary">
              <h2 className="text-lg font-bold text-gray-900 mb-5 uppercase tracking-wide">Order Summary</h2>
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <img src={item.product?.image_url || item.product?.imageUrl} alt={item.product?.title}
                      className="w-16 h-20 object-cover rounded-lg border border-gray-200" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 truncate">{item.product?.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Size: {item.size}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                      <p className="font-semibold text-gray-900 mt-1">${((item.price || item.product?.price || 0) * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Promo Code Input */}
              <div className="border-t border-gray-200 pt-4 mb-4">
                {promoApplied ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2.5" data-testid="promo-applied">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-700">{promoApplied.code}</span>
                      <span className="text-xs text-green-600">-${promoApplied.discount_amount.toFixed(2)}</span>
                    </div>
                    <button onClick={removePromo} className="p-1 hover:bg-green-100 rounded" data-testid="remove-promo">
                      <X className="w-3.5 h-3.5 text-green-600" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                          placeholder="Promo code"
                          className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase"
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                          data-testid="promo-code-input"
                        />
                      </div>
                      <button
                        onClick={handleApplyPromo}
                        disabled={promoLoading || !promoInput.trim()}
                        className="px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 disabled:opacity-40 transition-colors"
                        data-testid="apply-promo-btn">
                        {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </button>
                    </div>
                    {promoError && <p className="text-xs text-red-500 mt-1.5" data-testid="promo-error">{promoError}</p>}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900 font-medium">${total.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Discount ({promoApplied?.code})</span>
                    <span className="text-green-600 font-medium">-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className={`font-medium ${shippingCost === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                    {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                {subtotalAfterDiscount < shippingConfig.free_shipping_threshold && shippingCost > 0 && (
                  <p className="text-xs font-medium bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                    Add ${(shippingConfig.free_shipping_threshold - subtotalAfterDiscount).toFixed(2)} more for free shipping!
                  </p>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-base font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-black text-gray-900">${finalTotal.toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-gray-200 space-y-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>SSL Encrypted & Secure Checkout</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Truck className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span>{subtotalAfterDiscount >= shippingConfig.free_shipping_threshold ? 'Free shipping included!' : `Free shipping on orders $${shippingConfig.free_shipping_threshold.toFixed(0)}+`}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
